import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Meetings from "./components/Meetings";
import NewMeeting from "./components/NewMeeting";
import MeetingSummaryPage from "./components/MeetingSummaryPage";
import AuthCallback from "./components/AuthCallback";
import SSOCallback from "./components/SSOCallback";
import Header from "./components/Header";
import LoginScreen from "./components/LoginScreen";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { RoleProvider } from "./contexts/RoleContext";
import Groups from "./components/Groups";
import GroupDetails from "./components/GroupDetails";
import CustomerInsightsDashboard from "./components/customer-insights-dashboard";
import MeetingAnalytics from "./components/MeetingAnalytics";
import AdminPanel from "./components/AdminPanel";
import CallingAgent from "./components/CallingAgent";
import KnowledgeBase from "./components/KnowledgeBase";
import ExtensionAccess from "./components/ExtensionAccess";

const SIMSHOW_SSO = import.meta.env.VITE_SIMSHOW_SSO_ENABLED === "true";

// Main App component wrapped with authentication logic
const AppContent = () => {
  const [currentTab, setCurrentTab] = useState("meetings");
  const { isAuthenticated, isLoading, user, refreshTokenIfStale } = useAuth();

  // Proactively renew the SSO token periodically and when the tab regains
  // focus, so a stale token doesn't force a mid-session logout.
  // Only active when SimShow SSO integration is enabled.
  useEffect(() => {
    if (!SIMSHOW_SSO || !isAuthenticated || user?.source !== "sso") return;

    const CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

    const check = () => refreshTokenIfStale();

    // Re-check when tab becomes visible again (user switches back)
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") check();
    };

    const intervalId = setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isAuthenticated, user, refreshTokenIfStale]);

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading Sales Bot...</p>
        </div>
        <style jsx>{`
          .loading-container {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
              sans-serif;
          }

          .loading-content {
            text-align: center;
            color: white;
          }

          .loading-spinner {
            width: 48px;
            height: 48px;
            border: 4px solid rgba(255, 255, 255, 0.2);
            border-top-color: white;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 20px;
          }

          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }

          .loading-text {
            font-size: 18px;
            font-weight: 500;
            margin: 0;
          }
        `}</style>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  // Safety net: only SSO users (from SimShow) may use the app when SSO is enabled.
  if (SIMSHOW_SSO && user?.source !== "sso") {
    return (
      <div style={ssoOnlyStyles.container}>
        <div style={ssoOnlyStyles.card}>
          <h2 style={ssoOnlyStyles.title}>Access via SimShow Required</h2>
          <p style={ssoOnlyStyles.message}>
            SalesBot is only accessible through the SimShow portal. Please log
            in to SimShow and open SalesBot from there.
          </p>
          <button
            style={ssoOnlyStyles.btn}
            onClick={async () => {
              const { default: webAuthService } = await import(
                "./services/webAuthService"
              );
              await webAuthService.logout();
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // Show main app if authenticated
  return (
    <div className="app">
      <Routes>
          {/* Public routes */}
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Protected routes */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Header
                  currentTab={currentTab}
                  setCurrentTab={setCurrentTab}
                  user={user}
                />
                <main className="main-content">
                  <Routes>
                    <Route
                      path="/"
                      element={<Navigate to="/meetings" replace />}
                    />
                    <Route path="/meetings" element={<Meetings />} />
                    <Route path="/new-meeting" element={<NewMeeting />} />
                    <Route path="/calling-agent" element={<CallingAgent />} />
                    <Route path="/groups" element={<Groups />} />
                    <Route path="/groups/:groupId" element={<GroupDetails />} />
                    <Route path="/admin" element={<AdminPanel />} />
                    <Route path="/knowledge-base" element={<KnowledgeBase />} />
                    <Route
                      path="/meeting-summary/:meetingId"
                      element={<MeetingSummaryPage />}
                    />
                    <Route
                      path="/meeting-analytics/:meetingId"
                      element={<MeetingAnalytics />}
                    />
                    <Route
                      path="/group-analytics/:groupId"
                      element={<MeetingAnalytics />}
                    />
                    <Route
                      path="/my-analytics"
                      element={<MeetingAnalytics />}
                    />
                    <Route
                      path="/test/customer-insights"
                      element={<CustomerInsightsDashboard />}
                    />
                    <Route
                      path="/extension-access"
                      element={<ExtensionAccess />}
                    />
                  </Routes>
                </main>
              </ProtectedRoute>
            }
          />
      </Routes>
    </div>
  );
};

const ssoOnlyStyles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    background: "white",
    borderRadius: "12px",
    padding: "40px 48px",
    maxWidth: "420px",
    width: "100%",
    textAlign: "center",
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  },
  title: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#1a1a2e",
    marginBottom: "16px",
  },
  message: {
    fontSize: "15px",
    color: "#555",
    lineHeight: "1.6",
    marginBottom: "28px",
  },
  btn: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "12px 28px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
  },
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <RoleProvider>
          <Router>
            <Routes>
              {/* Public – reachable before auth check */}
              <Route path="/sso-callback" element={<SSOCallback />} />
              {/* Everything else goes through AuthContext */}
              <Route path="/*" element={<AppContent />} />
            </Routes>
          </Router>
        </RoleProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
